import { useEffect, useRef, useState } from 'react';
import CustomSelect from '@/components/Forms/CustomSelect';
import ChevronDown from '@/icons/ChevronDown';
import { type FilterOption, type Filter } from './Locator.types';

export type LocatorFiltersProps = {
  active: boolean;
  filterOptions: FilterOption;
  filters: Filter;
  onBack?: () => void;
  onFilter?: (selectedFilters: object) => void;
};

const LocatorFilters: React.FC<LocatorFiltersProps> = ({
  active,
  filterOptions,
  filters,
  onBack,
  onFilter,
}) => {
  const backButton = useRef(null);

  useEffect(() => {
    backButton.current?.focus();
  }, [active]);

  useEffect(() => {
    const handleKeydown = (e) => {
      // tab key press
      if (e.key === 'Tab' && e.keyCode == 9) {
        if (document.activeElement.id === 'locater-filters-submit') {
          setTimeout(() => {
            backButton.current?.focus();
          }, 0);
        }
      }
    };

    document.addEventListener('keydown', handleKeydown);

    return () => {
      document.removeEventListener('keydown', handleKeydown);
    };
  }, []);

  // extract current filter values
  const { services, type, fuel } = filters;

  // using state to track multi values of react-select component
  const [stationServices, setStationServices] = useState(services);

  // create DOM reference to the form so we can extract form data easily
  const form = useRef();

  // prevent scrolling when open
  useEffect(() => {
    const html = document.querySelector('html');

    html.style.overflowY = active ? 'hidden' : 'visible';

    if (active) {
      // scroll to top of the page
      window.scrollTo(0, 0);
    }
  }, [active]);

  // update local state when services filter state updates
  useEffect(() => {
    setStationServices(services);
  }, [services]);

  /**
   * Accumulate the form data and let the parent Locator component apply the
   * selected filters.
   *
   * @param {SubmitEvent} event
   * @return void
   */
  const handleSubmit = (event) => {
    if (event) {
      event.preventDefault();
    }

    // simple way to extract values from form
    const data = new FormData(form.current);
    const dataType = data.get('type');
    const dataFuel = data.get('fuel');

    const selectedFilters = {
      services: stationServices,
      type: dataType as string,
      fuel: dataFuel as string,
    };

    onFilter(selectedFilters);

    // update the URL with the new filters
    if ('URLSearchParams' in window) {
      const searchParams = new URLSearchParams(window.location.search);

      if (!dataType) {
        delete selectedFilters.type;
        searchParams.delete('t');
      } else {
        searchParams.set('t', selectedFilters.type);
      }

      if (!stationServices.length) {
        delete selectedFilters.services;
        searchParams.delete('s');
      } else {
        searchParams.set(
          's',
          Object.entries(selectedFilters.services)
            .map(([key, value]) => value)
            .join('__'),
        );
      }

      if (!dataFuel) {
        delete selectedFilters.fuel;
        searchParams.delete('f');
      } else {
        searchParams.set('f', selectedFilters.fuel);
      }

      if (searchParams.toString() !== '') {
        const newRelativePathQuery = `${
          window.location.pathname
        }?${searchParams.toString()}`;
        window.history.pushState(null, '', newRelativePathQuery);
      } else {
        window.history.pushState(null, '', window.location.pathname);
      }
    }

    // and close on mobile
    onBack();

    try {
      if (typeof window.dataLayer !== 'undefined' && selectedFilters) {
        window.dataLayer.push({
          event: 'component_interaction',
          component_type: 'filter',
          component_name: 'Find a station',
          interaction_type: 'click',
          services: Object.entries(selectedFilters.services || {})
            .map(([key, value]) => value)
            .join(','),
          station_type: selectedFilters.type,
          fuel_type: selectedFilters.fuel,
        });
      }
    } catch (e) {
      console.error(e);
    }

    return false;
  };

  /**
   * Used to keep track of values in the select component so we can send it
   * through to the parent Locator component because it does not store the
   * values in the DOM like the other fields.
   *
   * @param {Array} value
   * @return void
   */
  const handleFilterServices = (value) => {
    const updated =
      value.length > 0 ? value.map((station) => station.value) : [];

    setStationServices(updated);
  };

  // extract options to render out for fields below
  const { fuels, services: serviceOptions, types } = filterOptions;

  return (
    <div
      className={[
        'locator-filters',
        active ? 'locator-filters--active' : null,
      ].join(' ')}
      tabIndex={active ? 0 : -1}
      aria-hidden={active ? false : true}
    >
      <div className="locator-filters__container container">
        <button
          type="button"
          ref={backButton}
          className="locator-filters__back"
          onClick={onBack}
        >
          <ChevronDown fill="#6C6C6C" width={8} />
          Back
        </button>
        <form className="locator-filters__form" ref={form}>
          <div className="locator-filters__field locator-filters__field--services">
            <label className="locator-filters__label" htmlFor="services">
              Services
            </label>
            <CustomSelect
              className="locator-filters__services-field"
              classNamePrefix="services-field"
              isMulti
              name="services"
              id="services"
              onChange={(e) => {
                handleFilterServices(e);
              }}
              options={serviceOptions}
              placeholder="Select a service or services"
              value={serviceOptions.filter((o) =>
                stationServices.includes(o.value),
              )}
            />
          </div>
          <div className="locator-filters__field locator-filters__field--type">
            <label className="locator-filters__label">Station type</label>
            <select
              className="field__input field__input--select"
              name="type"
              defaultValue={type}
            >
              <option value="">Select station type</option>
              {types.length > 0 &&
                types.map((service) => {
                  const { label, value } = service;

                  return (
                    <option key={value} value={value}>
                      {label}
                    </option>
                  );
                })}
            </select>
          </div>
          <div className="locator-filters__field locator-filters__field--fuel">
            <label className="locator-filters__label">Fuel type</label>
            <select
              className="field__input field__input--select"
              name="fuel"
              defaultValue={fuel}
            >
              <option value="">Select fuel type</option>
              {fuels.length > 0 &&
                fuels.map((service) => {
                  const { label, value } = service;

                  return (
                    <option key={value} value={value}>
                      {label}
                    </option>
                  );
                })}
            </select>
          </div>
          <button
            className="locator-filters__apply button button--text-only"
            id="locater-filters-submit"
            onClick={handleSubmit}
            type="submit"
          >
            Apply filters
          </button>
        </form>
      </div>
    </div>
  );
};

export default LocatorFilters;
